import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ImagePreviewModal from './ImagePreviewModal';

/**
 * Image stage labels
 */
const STAGE_LABELS = {
    INITIAL: { label: 'Initial', color: 'primary' },
    IN_PROGRESS: { label: 'In Progress', color: 'warning' },
    COMPLETED: { label: 'Completed', color: 'success' },
};

/**
 * ImageGalleryModal - Full-screen gallery view of all images (read-only).
 * 
 * @param {boolean} open
 * @param {function} onClose
 * @param {Array} images - Array of image objects { id, url, thumbnailUrl, stage }
 * @param {boolean} showStageLabels (default: true)
 * @param {function} [onRequestUrl] - Async callback(imageId) => { url, thumbnailUrl }. Forwarded to preview modal.
 */
export default function ImageGalleryModal({
    open,
    onClose,
    images = [],
    showStageLabels = true,
    onRequestUrl,
}) {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);

    const handleImageClick = (index) => {
        setPreviewIndex(index);
        setPreviewOpen(true);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen
                PaperProps={{ sx: { bgcolor: 'background.default' } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6">
                        All Photos ({images.length})
                    </Typography>
                    <IconButton onClick={onClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1,
                        }}
                    >
                        {images.map((image, index) => (
                            <Box
                                key={image.id || index}
                                sx={{
                                    position: 'relative',
                                    paddingTop: '100%',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'grey.200',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={image.thumbnailUrl || image.url}
                                    alt={`Photo ${index + 1}`}
                                    onClick={() => handleImageClick(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                    }}
                                />

                                {/* Stage Label */}
                                {showStageLabels && image.stage && STAGE_LABELS[image.stage] && (
                                    <Chip
                                        label={STAGE_LABELS[image.stage].label}
                                        color={STAGE_LABELS[image.stage].color}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            left: 4,
                                            fontSize: '0.6rem',
                                            height: 18,
                                            '& .MuiChip-label': { px: 0.5 },
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Image Preview Modal */}
            <ImagePreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                images={images}
                initialIndex={previewIndex}
                onRequestUrl={onRequestUrl}
            />
        </>
    );
}
